// =============================================================================
// Default data builder — defines the unified schema (see ./schema.js)
// and providing initial empty states for the dashboards.
//
// The provider calls `getDefaultData(range)` to initialize state; the SDK
// in production replaces this entirely via `setSnapshot` / `setAll`.
// =============================================================================

import { COUNTRY_TZ } from "../Dashboards/_shared/constants";

// ── Helpers ──────────────────────────────────────────────────────────────────

// Days in each TimeRange. 24h is treated as 1 day for slicing math.
const RANGE_DAYS = { "24h": 1, "7d": 7, "14d": 14, "30d": 30 };

const daysFor = (range) => {
  if (range && typeof range === "object" && range.type === "custom") {
    const start = new Date(range.startDate).getTime();
    const end   = new Date(range.endDate).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 7;
    return Math.max(1, Math.round((end - start) / 86_400_000) + 1);
  }
  return RANGE_DAYS[range] ?? 7;
};

export const rangeWindow = (range, anchor = Date.now()) => {
  if (range && typeof range === "object" && range.type === "custom") {
    const start = new Date(range.startDate).getTime();
    const end   = new Date(range.endDate).getTime();
    if (Number.isFinite(start) && Number.isFinite(end)) {
      return { start, end };
    }
  }
  const days = daysFor(range);
  const end = anchor;
  return { start: end - days * 86_400_000, end };
};

export const tzFor = (country) => COUNTRY_TZ[country] || "UTC";

// ── Pure Logic Builders (Empty Initial States) ───────────────────────────────

function buildActivity() { return []; }
function buildPrivilegedDaily() { return []; }
function buildDeniedDaily() { return []; }
function buildStorage() { return { byType: [], byTeam: [], byRegion: [] }; }
function buildExternalShareFlow() { return []; }
function buildTopMovers() { return { downloads: [], uploads: [], shares: [], activity: [] }; }
function buildLocations() { return []; }
function buildRecentActivity() { return []; }
function buildOpMix() { return []; }

function buildKpis() {
  return {
    activeUsers: 0,
    removedUsers: 0,
    externalShares: 0,
    privilegedTotal: 0,
    deniedTotal: 0,
    storageUsedGb: 0,
    storageTotalGb: 0,
    seatsUsed: 0,
    seatsPurchased: 0,
    externalUsers: 0,
    spaces: 0,
    activityWindow: { start: "", end: "" },
  };
}

// ── Top-level builders ───────────────────────────────────────────────────────

function buildOverview() {
  return {
    org: { name: "Organization", plan: "Enterprise", employees: 0, location: "Unknown" },
    kpis: buildKpis(),
    activity: buildActivity(),
    storage: buildStorage(),
    privilegedDaily: buildPrivilegedDaily(),
    externalShareFlow: buildExternalShareFlow(),
    topMovers: buildTopMovers(),
    externalShareRankByUser: [],
    externalShareRankByTeam: [],
    locations: buildLocations(),
    recentActivity: buildRecentActivity(),
    offHoursRatio: { offHours: 0, total: 0 },
    deniedDaily: buildDeniedDaily(),
    topOperations: buildOpMix(),
  };
}

function buildFiles() {
  return {
    files: [],
    folderTree: [],
    lineage: [],
    fileEvents: {},
    currentShares: {},
    pathHistory: {},
    externalExposure: {},
    selectedFileUuid: null,
    breadcrumb: [],
  };
}

function buildUsersTeams() {
  return {
    users: [],
    teams: [],
    userActivity: {},
    teamActivity: {},
    knownDomains: [],
    selectedSubject: null,
    selectedSubjectStack: [],
    recentSubjects: [],
    anomalies: {},
    riskScores: {},
    permissionGraph: {},
    permissionGraphPrev: {},
  };
}

function buildLocationsData() {
  return {
    access: [],
    trustedRegions: [],
    compromisedLogs: [],
  };
}

function buildAnalysis() {
  return { history: [] };
}

export function getDefaultData(range) {
  return {
    overview: buildOverview(),
    files: buildFiles(),
    users: buildUsersTeams(),
    locations: buildLocationsData(),
    analysis: buildAnalysis(),
  };
}

// ── User Activity Aggregation Logic (Pure Functions) ─────────────────────────

function _buildHourHeatmap(events) {
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  events.forEach((e) => {
    const d = new Date(e.timestamp);
    const dow = (d.getUTCDay() + 6) % 7;
    grid[dow][d.getUTCHours()] += 1;
  });
  return grid;
}

function _buildOpMix(events) {
  const counts = {};
  events.forEach((e) => { counts[e.operation] = (counts[e.operation] || 0) + 1; });
  return Object.entries(counts)
    .map(([operation, count]) => ({ operation, count }))
    .sort((a, b) => b.count - a.count);
}

function _buildDaily(events) {
  const buckets = {};
  events.forEach((e) => {
    const date = new Date(e.timestamp).toISOString().slice(0, 10);
    if (!buckets[date]) buckets[date] = { date, reads: 0, writes: 0, shares: 0, deletes: 0 };
    const op = e.operation;
    if (op === "GetChunk") buckets[date].reads += 1;
    else if (["StartWrite", "FinalizeWrite", "RestoreVersion"].includes(op)) buckets[date].writes += 1;
    else if (["Share", "UpdateShare"].includes(op)) buckets[date].shares += 1;
    else if (op && op.startsWith("Remove")) buckets[date].deletes += 1;
  });
  return Object.values(buckets).sort((a, b) => a.date.localeCompare(b.date));
}

export function buildUserBundle(events, user) {
  return {
    events,
    daily: _buildDaily(events),
    hourHeatmap: _buildHourHeatmap(events),
    opMix: _buildOpMix(events),
    countries: [], // Rebuilt in dashboard if needed
    sessions: [],
    filesTouched: [],
    sharesGranted: [],
    deniedAttempts: events.filter((e) => e.success === false),
    permissionsReceived: [],
    permissionsDelta: { added: [], removed: [] },
    baseline: { countriesEverSeen: [], lastActiveBeforeRange: 0 },
  };
}

export const RANGE_OPTIONS = ["24h", "7d", "14d", "30d"];
export const DEFAULT_RANGE = "7d";
